import {t} from '@lingui/macro';
import {titleCase} from 'title-case';

import {shortcodeToUnified} from '../../../srcCommon/helpers/emoji/emojiTextHelpers';
import {LocalizeRenderer} from '../../../srcCommon/internationalization/internationalizationContext';
import {transformEmojis} from '../../helpers/emoji/emojiAndEmoticonsTextHelpers';
import {buildTeammateDisplayName} from '../../helpers/modelHelpers';
import {
  ActivityModel,
  ActivityVisualTypesEnum,
  BasicActivityModel,
  BasicActivityTypesEnum,
  CommentActivityTypesEnum,
} from '../../models/api/activityModel';
import {ActivitySourceVisualTypesEnum} from '../../models/api/activitySourceModel';
import {MessageTypesEnum} from '../../models/api/messageModel';
import {NotificationModel} from '../../models/api/notificationModel';
import {shouldShowNotificationsUsingElectron} from '../../services/electron/electronBridge';
import {showNotificationUsingElectron} from '../../services/electron/electronBridgeOutbound';
import {baseEmojiValidationMap} from '../../services/emojis/emojis';
import {
  NotificationOptions,
  NotificationResultsEnum,
  showNotificationAsync,
} from '../../services/notifications';
import {AppState} from '../appState';
import {ThunkAction} from '../appThunk';
import {getFindTeammate} from '../data/teammates/lazyTeammatesSelectors';
import {getLocalize, Localizer} from './internationalizationSelectors';
import {navigateToLinkAction} from './navigationActions';

/*
 * Action creators.
 */

export function showAppNotificationAction(notification: NotificationModel): ThunkAction {
  return async (dispatch, getState) => {
    const state = getState();
    const localize = getLocalize(state);

    // Build the notification options. If none are found, nothing to do.
    const notificationOptions = buildNotification(localize, state, notification);
    if (!notificationOptions) {
      return;
    }

    const link = notification.p2FiltersLink;

    dispatch(showNotificationAction(notificationOptions, link));
  };
}

export function showNotificationAction(notificationOptions: NotificationOptions, link: string): ThunkAction {
  return async (dispatch) => {
    if (shouldShowNotificationsUsingElectron()) {
      showNotificationUsingElectron(notificationOptions, link);
      return; // Electron will handle navigating on click, so nothing more to do.
    }

    // Display the notification.
    const result = await showNotificationAsync(notificationOptions);
    // If it's not clicked, nothing to do.
    if (result !== NotificationResultsEnum.CLICKED) {
      return;
    }

    // Otherwise, navigate to the activity link.
    dispatch(navigateToLinkAction(link));
  };
}

function buildNotification(localize: Localizer, state: AppState, notification: NotificationModel) {
  const {activity} = notification;
  switch (activity.visualType) {
    case ActivityVisualTypesEnum.COMMENT:
      return buildCommentNotification(localize, state, notification);

    case ActivityVisualTypesEnum.MESSAGE:
      return buildMessageNotification(localize, notification);

    case ActivityVisualTypesEnum.BASIC:
      return buildBasicNotification(localize, notification, activity);

    default:
      return null;
  }
}

/*
 * Comment.
 */

function buildCommentNotification(
  localize: Localizer,
  state: AppState,
  notification: NotificationModel,
): NotificationOptions {
  // Find the teammate avatar to display, if any.
  const {activity, fromTeammateId, summary} = notification;
  const findTeammate = getFindTeammate(state);
  const author = (fromTeammateId && findTeammate(fromTeammateId, 'buildCommentNotification')) || undefined;
  const authorName = (author && buildTeammateDisplayName(author)) || notification.fromName;
  const authorAvatarUrl = author && author.avatarUrl;

  // Build the notification options.
  return {
    title: buildCommentTitle(localize, activity, authorName),
    body:
      summary &&
      transformEmojis(summary, baseEmojiValidationMap, (_key, _image, emoji) =>
        shortcodeToUnified(emoji),
      ).join(''),
    iconUrl: authorAvatarUrl,
  };
}

function buildCommentTitle(localize: Localizer, activity: ActivityModel, authorName: string) {
  // build the title.
  switch (activity.type) {
    case CommentActivityTypesEnum.MENTION:
      return localize(t('notification-title-mention')`Mentioned by ${authorName}`);

    default:
      return localize(t('notification-title-comment')`Comment from ${authorName}`);
  }
}

/*
 * Message.
 */

function buildMessageNotification(localize: Localizer, notification: NotificationModel): NotificationOptions {
  return {
    title: buildMessageTitle(localize, notification),
    body: notification.summary,
  };
}

function buildMessageTitle(localize: Localizer, {fromName, messageType}: NotificationModel) {
  // We need a Message Type.
  if (!messageType) {
    throw new Error('A message type was not provided for the specified message activity.');
  }

  // Build the localized version of the Message Type.
  const displayMessageType = titleCase(messageTypesRenderer[messageType](localize));

  // If no recipient name was provided, render a generic message.
  if (!fromName) {
    return displayMessageType;
  }

  // Otherwise, include the name.
  return localize(t('notification-title-message')`${displayMessageType} from ${fromName}`);
}

type MessageTypesRenderer = {[T in MessageTypesEnum]: LocalizeRenderer};
const messageTypesRenderer: MessageTypesRenderer = {
  [MessageTypesEnum.INTERNAL]: (localize) => localize(t('message-type-internal')`internal chat`),
  [MessageTypesEnum.EMAIL]: (localize) => localize(t('message-type-email')`email`),
  [MessageTypesEnum.TWEET]: (localize) => localize(t('message-type-tweet')`tweet`),
  [MessageTypesEnum.TWEET_DM]: (localize) => localize(t('message-type-twitterdm')`dm`),
  [MessageTypesEnum.CALL]: (localize) => localize(t('message-type-call')`phone call`),
  [MessageTypesEnum.SMS]: (localize) => localize(t('message-type-sms')`sms`),
  [MessageTypesEnum.GOOGLE_PLAY]: (localize) => localize(t('message-type-googleplay')`google play review`),
  [MessageTypesEnum.FACEBOOK]: (localize) => localize(t('message-type-facebook')`facebook message`),
  [MessageTypesEnum.INTERCOM]: (localize) => localize(t('message-type-intercom')`intercom message`),
  [MessageTypesEnum.SMOOCH]: (localize) => localize(t('message-type-smooch')`smooch message`),
  [MessageTypesEnum.LAYER]: (localize) => localize(t('message-type-layer')`chat`),
  [MessageTypesEnum.YALO_WHATSAPP]: (localize) => localize(t('message-type-whatsapp')`whatsapp message`),
  [MessageTypesEnum.WHATSAPP]: (localize) => localize(t('message-type-whatsapp')`whatsapp message`),
  [MessageTypesEnum.CUSTOM]: (localize) => localize(t('message-type-custom')`custom message`),
  [MessageTypesEnum.FRONT_CHAT]: (localize) => localize(t('message-type-frontchat')`chat`),
};

/*
 * Basic.
 */

function buildBasicNotification(
  localize: Localizer,
  notification: NotificationModel,
  activity: BasicActivityModel,
): NotificationOptions | null {
  const title = buildBasicTitle(localize, activity);
  if (!title) {
    return null;
  }

  return {
    title,
    body: notification.summary,
  };
}

function buildBasicTitle(localize: Localizer, activity: BasicActivityModel) {
  switch (activity.type) {
    case BasicActivityTypesEnum.ASSIGN:
      return localize(t('notification-title-assign')`Conversation assigned`);

    case BasicActivityTypesEnum.UNASSIGN:
      return localize(t('notification-title-unassign')`Conversation unassigned`);

    case BasicActivityTypesEnum.ARCHIVE:
      return localize(t('notification-title-close')`Conversation closed`);

    case BasicActivityTypesEnum.REOPEN:
      return localize(t('notification-title-reopen')`Conversation reopened`);

    case BasicActivityTypesEnum.REMINDER:
    case BasicActivityTypesEnum.TRACKER_REMINDER:
      return localize(t('notification-title-reminder')`Conversation reminder`);

    case BasicActivityTypesEnum.RULE_NOTIFY:
      return buildRuleTitle(localize, activity);

    case BasicActivityTypesEnum.MOVE:
      return localize(t('notification-title-move')`Conversation moved into inbox`);

    default:
      return null;
  }
}

function buildRuleTitle(localize: Localizer, activity: BasicActivityModel) {
  // Find the name of the rule that created the activity.
  const {source} = activity;
  const ruleName = source.visualType === ActivitySourceVisualTypesEnum.RULE && source.ruleName;

  // If a name could not found, render a generic message.
  if (!ruleName) {
    return localize(t('notification-title-rule-generic')`Notification from rule`);
  }

  // Otherwise, include the rule name.
  return localize(t('notification-title-rule')`Notification from ${ruleName}`);
}
